import {useEffect, useRef, useState} from "react";

interface VariableListNavigationOptions<T> {
    /** The rows in display order. */
    items: readonly T[];
    onSelect: (item: T) => void;
    /** Changing this resets selection to the first result. */
    resetKey: string;
    /** For each row, its index among the list's entries (headings included). */
    rowEntryIndex: readonly number[];
    /** Brings one entry into view, nearest edge; the list owns the scroll element. */
    scrollToIndex: (entryIndex: number) => void;
}

/**
 * Pointer and keyboard selection for the variable list.
 *
 * The semantics of the shared `usePickerNavigation` (clamp at the ends, Enter/Tab select, pointer
 * de-duplication), which is not edited from here. The list is virtualized, so a row that is off
 * screen is not in the DOM to `scrollIntoView`; scrolling goes through the list's `scrollToIndex`.
 */
export function useVariableListNavigation<T>({
    items,
    onSelect,
    resetKey,
    rowEntryIndex,
    scrollToIndex,
}: VariableListNavigationOptions<T>) {
    const [storedIndex, setSelectedIndex] = useState(0);
    const cameFromKeyboard = useRef(false);
    const pointerAt = useRef<{x: number; y: number} | null>(null);

    /*
        A new query starts on the first result. Reset during render rather than in an effect, so
        the first paint of the new results already highlights the right row.
     */
    const [seenResetKey, setSeenResetKey] = useState(resetKey);
    let wanted = storedIndex;
    if (seenResetKey !== resetKey) {
        setSeenResetKey(resetKey);
        wanted = 0;
    }

    // A list that shrank under the selection keeps its last row selected.
    const selectedIndex = Math.min(wanted, Math.max(items.length - 1, 0));
    if (selectedIndex !== storedIndex) setSelectedIndex(selectedIndex);

    const latest = useRef({rowEntryIndex, scrollToIndex});
    latest.current = {rowEntryIndex, scrollToIndex};

    useEffect(() => {
        if (!cameFromKeyboard.current) return;

        const {rowEntryIndex: entryOf, scrollToIndex: scrollTo} = latest.current;
        const entry = entryOf[selectedIndex];
        if (entry === undefined) return;

        /*
            A grouped list arrowed into from below otherwise stops with the first row of a group
            flush against the top edge and the heading that names it just out of sight. A gap in
            the entry indices means a heading sits right above the row.
         */
        const previous = selectedIndex > 0 ? entryOf[selectedIndex - 1] : -1;
        if (entry - previous > 1) scrollTo(entry - 1);

        scrollTo(entry);
    }, [selectedIndex]);

    const selectFromPointer = (index: number, x: number, y: number) => {
        const last = pointerAt.current;
        if (last && last.x === x && last.y === y) return;
        pointerAt.current = {x, y};
        cameFromKeyboard.current = false;
        setSelectedIndex(index);
    };

    const selectFromKeyboard = (index: number) => {
        cameFromKeyboard.current = true;
        setSelectedIndex(index);
    };

    const handleKey = (key: string): boolean => {
        if (items.length === 0) return false;
        if (key === "ArrowDown") {
            selectFromKeyboard(Math.min(selectedIndex + 1, items.length - 1));
            return true;
        }
        if (key === "ArrowUp") {
            selectFromKeyboard(Math.max(selectedIndex - 1, 0));
            return true;
        }
        if ((key === "Enter" || key === "Tab") && items[selectedIndex]) {
            onSelect(items[selectedIndex]);
            return true;
        }
        return false;
    };

    return {selectedIndex, selectFromPointer, handleKey};
}

export default useVariableListNavigation;
